import { useEffect, useId, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";

import {
  acknowledgementCopy,
  acknowledgeSosSignal,
  activeSosAlerts,
  appendSosResponseNote,
  buildSosTimeline,
  claimSosLead,
  clearedSosAlerts,
  dutyFreshness,
  fetchSosResponseLog,
  fetchSosSignalQueue,
  formatAge,
  releaseSosLead,
  standDownSosAlert,
  validateHandOffReason,
  validateResponseNote,
  validateStandDown,
  DUTY_COORD_DECIMALS,
  DUTY_POSITION_POST_FALLBACK,
  DUTY_POSITION_UNAVAILABLE,
  hasDutyPosition,
  SOS_ACTION_UNAVAILABLE,
  SOS_LOG_UNAVAILABLE,
  SOS_LOG_UNAVAILABLE_DETAIL,
  SOS_QUEUE_EMPTY,
  SOS_QUEUE_EMPTY_TITLE,
  SOS_QUEUE_CAPPED_DETAIL,
  SOS_QUEUE_UNAVAILABLE,
  SOS_QUEUE_UNAVAILABLE_DETAIL,
  STAND_DOWN_NOTE_MAX,
  STAND_DOWN_OUTCOME_REQUIRED,
  STAND_DOWN_OUTCOMES,
  standDownOutcomeLabel,
  type SosLogEntry,
  type SosSignal,
  type StandDownOutcomeKey,
} from "../data/sos-desk";
import { useReadModel } from "../data/useReadModel";
import { fetchSosCreationState } from "../data/sos-response-only";
import { SosResponseOnlyPanel } from "./SosResponseOnlyPanel";
import { useSupportSession } from "../support/SupportSessionProvider";
import { isSupportSessionActive } from "../data/support-session";
import { formatDisplayTime } from "../lib/display-time";

// The Console response surface for one open SOS: arrive, acknowledge, take the lead, read
// where the officer was last reported, reach them from your own phone, stand down with an
// outcome. One door: Command Center's SOS tile and Inbox Critical both open this, and
// neither Inbox nor Dispatch carries an act of its own.
//
//   * Acknowledge is RECORDED and non-exclusive. Many supervisors may acknowledge the same
//     signal. The surface states a count, never an owner badge the host cannot prove.
//   * Take the lead is additive and revocable. It is a label for the team, not a lock: it
//     never makes another supervisor's act impossible during a life-safety event.
//   * Nothing here sends help, dials anyone, or files an incident. No host write exists for
//     any of those, and a control that looked like it summoned help would be the worst
//     possible lie on this surface.
//   * Nothing on this surface animates for attention. The Command Center tile pulse is the
//     only repeating motion in the product.

/** The word "Desk" never appears in rendered copy at any width (shell v3 lock 10). */
const SURFACE_TITLE = "SOS response";

const REACH_COPY =
  "Reach them from your own phone. SecOps never contacts emergency services for you.";
const NO_DISPATCH_COPY =
  "Sending a named officer to this signal is not something this product can record yet.";
const SUPPORT_READ_ONLY = "Support session · Read-only SOS response";

export function SosResponseDesk({
  client,
  accountId,
  signals,
  log,
  timeZone,
  onReviewed,
  nowMs = Date.now(),
  canWrite = true,
  selectedAlertEventId = null,
}: {
  client: SupabaseClient;
  accountId: string;
  signals: readonly SosSignal[];
  /** Null when the recorded log read was unavailable. P-26: the timeline then shows no rows. */
  log: readonly SosLogEntry[] | null;
  timeZone: string;
  onReviewed: () => void;
  nowMs?: number;
  /** Console-side optimization only. The host holds the refusal on every write path. */
  canWrite?: boolean;
  selectedAlertEventId?: string | null;
}) {
  const active = activeSosAlerts(signals);
  const cleared = clearedSosAlerts(signals).slice(0, 3);

  const deepLinked =
    selectedAlertEventId !== null &&
    active.some((signal) => signal.alertEventId === selectedAlertEventId)
      ? selectedAlertEventId
      : null;

  // `pushed` is the 767px behaviour: below that width the detail pushes over the list and
  // Back restores it. Above that width CSS shows both and the flag only marks which row is
  // current. A deep link arrives already pushed, because the supervisor asked for a signal.
  const [openId, setOpenId] = useState<string | null>(deepLinked);
  const [pushed, setPushed] = useState(deepLinked !== null);
  const [lastDeepLink, setLastDeepLink] = useState<string | null>(deepLinked);

  if (lastDeepLink !== deepLinked) {
    setLastDeepLink(deepLinked);
    setOpenId(deepLinked);
    setPushed(deepLinked !== null);
  }

  // A signal that leaves the active set stops being the detail rather than leaving the pane
  // pointing at something the account can no longer read.
  const openStillActive = openId !== null && active.some((signal) => signal.alertEventId === openId);
  const detailId = openStillActive ? openId : (active[0]?.alertEventId ?? null);
  const detail = active.find((signal) => signal.alertEventId === detailId) ?? null;
  const single = active.length <= 1;

  if (active.length === 0) {
    return (
      <section className="sos sos--quiet" aria-label={SURFACE_TITLE}>
        <h2 className="sos__title">{SURFACE_TITLE}</h2>
        <p className="sos__empty-title">{SOS_QUEUE_EMPTY_TITLE}</p>
        <p className="surface__state" role="status">
          {SOS_QUEUE_EMPTY}
        </p>
        {cleared.length > 0 ? (
          <ClearedList alerts={cleared} log={log} timeZone={timeZone} nowMs={nowMs} />
        ) : null}
      </section>
    );
  }

  return (
    <section className="sos sos--active" aria-label={SURFACE_TITLE}>
      {/* Persistent, and re-rendered on every poll. role="alert" would re-interrupt a screen
          reader each time; the ribbon is a standing condition, so it is announced politely and
          the per-card controls carry the urgency. It does not animate. */}
      <p className="sos__ribbon" role="status">
        {active.length === 1 ? "Active SOS" : `${active.length} active SOS signals`}
      </p>
      <h2 className="sos__title">{SURFACE_TITLE}</h2>
      {!canWrite ? (
        <p className="sos__readonly" role="status">
          {SUPPORT_READ_ONLY}
        </p>
      ) : null}

      <div className={single || pushed ? "sos__split sos__split--pushed" : "sos__split"}>
        {!single ? (
          <ul className="sos__list" aria-label="Active SOS signals">
            {active.map((signal) => (
              <li key={signal.alertEventId}>
                <button
                  type="button"
                  className={
                    signal.alertEventId === detailId
                      ? "sos__list-row sos__list-row--on"
                      : "sos__list-row"
                  }
                  aria-current={signal.alertEventId === detailId ? "true" : undefined}
                  onClick={() => {
                    setOpenId(signal.alertEventId);
                    setPushed(true);
                  }}
                >
                  <span className="sos__badge">SOS</span>
                  <span className="sos__list-who">{signal.guardLabel ?? "Officer"}</span>
                  <span className="sos__list-post">{postLabel(signal)}</span>
                </button>
              </li>
            ))}
          </ul>
        ) : null}

        {detail !== null ? (
          <div className="sos__detail">
            {!single ? (
              // Visible only below 768px, where the detail pushes over the list. The control
              // stays in the DOM so focus order and the accessible name never change with
              // viewport width.
              <button type="button" className="sos__back" onClick={() => setPushed(false)}>
                Back
              </button>
            ) : null}
            <SosCard
              key={detail.alertEventId}
              client={client}
              accountId={accountId}
              signal={detail}
              log={log}
              timeZone={timeZone}
              onReviewed={onReviewed}
              nowMs={nowMs}
              canWrite={canWrite}
              selected={detail.alertEventId === deepLinked}
            />
          </div>
        ) : null}
      </div>

      {cleared.length > 0 ? (
        <ClearedList alerts={cleared} log={log} timeZone={timeZone} nowMs={nowMs} />
      ) : null}
    </section>
  );
}

/** Post first: a supervisor reads a place faster than a number (D-08). */
function postLabel(signal: SosSignal): string {
  if (signal.siteName !== null && signal.routeName !== null) {
    return `${signal.siteName} · ${signal.routeName}`;
  }
  return signal.siteName ?? signal.routeName ?? "Post not recorded";
}

function PositionBlock({ signal, nowMs }: { signal: SosSignal; nowMs: number }) {
  const freshness = dutyFreshness(signal, nowMs);
  const age = formatAge(signal.dutyPositionAt, nowMs);

  return (
    <>
      <p className="sos__post">{postLabel(signal)}</p>
      {hasDutyPosition(signal) ? (
        <>
          <p className="sos__freshness">
            <span className="sos__freshness-word">{freshness}</span>
            {age !== null ? (
              <span className="sos__freshness-age">Last seen {age} ago</span>
            ) : null}
          </p>
          <p className="sos__position-note">
            This is a last-known report, not continuous tracking.
          </p>
          {/* Secondary by design (D-08): the place above is what a supervisor acts on. Trimmed
              to about eleven metres, because the extra decimal a phone reports is below the
              accuracy of the fix itself and printing it would dress a rough position as a
              pinpoint. When the device reported no accuracy the surface says that too. */}
          <p className="sos__duty-coords">
            Last duty fix: {signal.dutyLat!.toFixed(DUTY_COORD_DECIMALS)},{" "}
            {signal.dutyLng!.toFixed(DUTY_COORD_DECIMALS)}
            {signal.dutyGpsAccuracyMeters !== null
              ? ` (accurate to about ${Math.round(signal.dutyGpsAccuracyMeters)} m)`
              : " (accuracy not reported)"}
          </p>
        </>
      ) : (
        <>
          <p className="sos__freshness">
            <span className="sos__freshness-word">No ping</span>
          </p>
          <p className="sos__position-note">{DUTY_POSITION_UNAVAILABLE}</p>
          <p className="sos__position-note">{DUTY_POSITION_POST_FALLBACK}</p>
        </>
      )}
    </>
  );
}

function SosCard({
  client,
  accountId,
  signal,
  log,
  timeZone,
  onReviewed,
  nowMs,
  canWrite,
  selected,
}: {
  client: SupabaseClient;
  accountId: string;
  signal: SosSignal;
  log: readonly SosLogEntry[] | null;
  timeZone: string;
  onReviewed: () => void;
  nowMs: number;
  canWrite: boolean;
  selected: boolean;
}) {
  const logNoteId = useId();
  const reasonCountId = useId();
  const noteCountId = useId();
  const [standingDown, setStandingDown] = useState(false);
  const [outcome, setOutcome] = useState<StandDownOutcomeKey | null>(null);
  const [note, setNote] = useState("");
  const [handingOff, setHandingOff] = useState(false);
  const [reason, setReason] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  const [responseNote, setResponseNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  // A support session or a lost key mid-typing clears every retained actionable state, so a
  // supervisor cannot submit a draft the host is about to refuse.
  useEffect(() => {
    if (canWrite) return;
    setStandingDown(false);
    setHandingOff(false);
    setAddingNote(false);
    setOutcome(null);
    setNote("");
    setReason("");
    setResponseNote("");
    setFailure(null);
  }, [canWrite]);

  const age = formatAge(signal.detectedAt, nowMs);
  const standDownReady = validateStandDown(outcome, note).ok;
  const reasonCheck = validateHandOffReason(reason);
  const responseNoteCheck = validateResponseNote(responseNote);
  const team = acknowledgementCopy(signal);
  const timeline = log === null ? [] : buildSosTimeline(signal, log);
  const someoneResponding = signal.status === "acknowledged" || signal.responseCount > 0;

  async function submit(work: () => Promise<{ ok: boolean; reason?: string }>): Promise<void> {
    if (busy) return;
    setBusy(true);
    setFailure(null);
    try {
      const result = await work();
      if (result.ok) {
        onReviewed();
        return;
      }
      setFailure(result.reason ?? SOS_ACTION_UNAVAILABLE);
    } catch {
      setFailure(SOS_ACTION_UNAVAILABLE);
    } finally {
      setBusy(false);
    }
  }

  return (
    <article
      className={selected ? "sos__card sos__card--deep-linked" : "sos__card"}
      aria-label={`SOS from ${signal.guardLabel ?? "an officer"}`}
      aria-current={selected ? "true" : undefined}
    >
      <div className="sos__head">
        <span className="sos__badge">SOS</span>
        <span className="sos__who">{signal.guardLabel ?? "Officer"}</span>
        {age !== null ? <span className="sos__age">pressed {age} ago</span> : null}
        {/* Names people, not the Dispatch surface: by X-07 Dispatch never carries SOS, so
            copy that said Dispatch was responding pointed at a surface that cannot respond. */}
        {someoneResponding ? (
          <span className="sos__working-badge" role="status">
            Supervisors responding
          </span>
        ) : null}
      </div>

      <PositionBlock signal={signal} nowMs={nowMs} />

      <p className="sos__reach">{REACH_COPY}</p>
      <p className="sos__no-dispatch">{NO_DISPATCH_COPY}</p>

      {/* Caller-bound, so it survives a reload. The host proves this acknowledgement; the
          count below is a separate fact and neither one is ownership. */}
      {signal.callerAcknowledged ? (
        <p className="sos__acknowledged" role="status">
          Acknowledged. Recorded with your name and the time.
        </p>
      ) : null}

      {team !== null ? (
        <p className="sos__team" role="status">
          {team}
        </p>
      ) : null}

      {canWrite && !signal.callerAcknowledged ? (
        <>
          <button
            type="button"
            className="sos__acknowledge"
            disabled={busy}
            onClick={() =>
              void submit(() => acknowledgeSosSignal(client, accountId, signal.alertEventId))
            }
          >
            Acknowledge
          </button>
          <p className="sos__local">
            Records your name and the time. Others on your team can acknowledge too.
          </p>
        </>
      ) : null}

      {canWrite && signal.callerAcknowledged ? (
        <p className="sos__local">
          Your team is on this signal. Others on your team can acknowledge it. Stand down when
          the situation is clear.
        </p>
      ) : null}

      <LeadBlock
        signal={signal}
        canWrite={canWrite}
        busy={busy}
        handingOff={handingOff}
        reason={reason}
        reasonCountId={reasonCountId}
        reasonReady={reasonCheck.ok}
        reasonBlocker={reasonCheck.ok ? null : reasonCheck.reason}
        onReason={setReason}
        onStartHandOff={() => {
          setHandingOff(true);
          setFailure(null);
        }}
        onCancelHandOff={() => {
          setHandingOff(false);
          setReason("");
          setFailure(null);
        }}
        onTake={() =>
          void submit(async () => {
            const result = await claimSosLead(
              client,
              accountId,
              signal.alertEventId,
              signal.leadVersion,
              null,
            );
            return result;
          })
        }
        onHandOff={() =>
          void submit(async () => {
            if (!reasonCheck.ok) return { ok: false, reason: reasonCheck.reason };
            const result = await claimSosLead(
              client,
              accountId,
              signal.alertEventId,
              signal.leadVersion,
              reasonCheck.value,
            );
            if (result.ok) {
              setHandingOff(false);
              setReason("");
            }
            return result;
          })
        }
        onRelease={() =>
          void submit(() =>
            releaseSosLead(client, accountId, signal.alertEventId, signal.leadVersion),
          )
        }
      />

      <SosTimeline entries={timeline} timeZone={timeZone} logUnavailable={log === null} />

      {/* Mid-response notes ship with the lead (O-06), so this box never appears before the
          host can store what a supervisor types during an emergency. */}
      {canWrite ? (
        addingNote ? (
          <div className="sos__note-add">
            <label className="sos__note">
              Note · internal
              <textarea
                rows={2}
                value={responseNote}
                maxLength={STAND_DOWN_NOTE_MAX}
                aria-describedby={noteCountId}
                placeholder="Add context for the response record"
                onChange={(event) => setResponseNote(event.target.value)}
              />
            </label>
            <p className="sos__counter" id={noteCountId}>
              {responseNote.trim().length} / {STAND_DOWN_NOTE_MAX}
            </p>
            <div className="sos__actions">
              <button
                type="button"
                className="sos__note-go"
                disabled={busy || !responseNoteCheck.ok}
                onClick={() =>
                  void submit(async () => {
                    const result = await appendSosResponseNote(
                      client,
                      accountId,
                      signal.alertEventId,
                      responseNote,
                    );
                    if (result.ok) {
                      setAddingNote(false);
                      setResponseNote("");
                    }
                    return result;
                  })
                }
              >
                Add to the record
              </button>
              <button
                type="button"
                className="builder__navbtn"
                disabled={busy}
                onClick={() => {
                  setAddingNote(false);
                  setResponseNote("");
                  setFailure(null);
                }}
              >
                Cancel
              </button>
            </div>
            {!responseNoteCheck.ok ? (
              <p className="sos__blocker">{responseNoteCheck.reason}</p>
            ) : null}
          </div>
        ) : (
          <div className="sos__actions">
            <button
              type="button"
              className="builder__navbtn"
              disabled={busy}
              onClick={() => {
                setAddingNote(true);
                setFailure(null);
              }}
            >
              Add a note
            </button>
          </div>
        )
      ) : null}

      {canWrite ? (
        standingDown ? (
          <div className="sos__standdown">
            <fieldset className="sos__outcomes">
              <legend className="sos__outcomes-legend">
                What happened <span className="sos__req">· required</span>
              </legend>
              <div className="sos__chips" role="radiogroup" aria-label="Stand down outcome">
                {STAND_DOWN_OUTCOMES.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    role="radio"
                    aria-checked={outcome === option.key}
                    className={`sos__chip${outcome === option.key ? " sos__chip--on" : ""}`}
                    disabled={busy}
                    onClick={() => setOutcome(option.key)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </fieldset>
            <label className="sos__note">
              Note (optional) · internal
              <textarea
                rows={2}
                value={note}
                maxLength={STAND_DOWN_NOTE_MAX}
                placeholder="Add context for the response record"
                onChange={(event) => setNote(event.target.value)}
              />
            </label>
            <p className="sos__log-note" id={logNoteId}>
              Recorded with your membership and the time. Outcome and note are stored
              separately. This log cannot be edited or deleted.
            </p>
            <div className="sos__actions">
              <button
                type="button"
                className="sos__standdown-go"
                disabled={busy || !standDownReady}
                aria-describedby={logNoteId}
                onClick={() =>
                  void submit(async () => {
                    const result = await standDownSosAlert(
                      client,
                      accountId,
                      signal.alertEventId,
                      outcome,
                      note,
                    );
                    if (result.ok) setStandingDown(false);
                    return result;
                  })
                }
              >
                Stand down and log
              </button>
              <button
                type="button"
                className="builder__navbtn"
                disabled={busy}
                onClick={() => {
                  setStandingDown(false);
                  setFailure(null);
                }}
              >
                Keep active
              </button>
            </div>
            {!standDownReady ? (
              <p className="sos__blocker">{STAND_DOWN_OUTCOME_REQUIRED}</p>
            ) : null}
          </div>
        ) : (
          <div className="sos__actions">
            <button
              type="button"
              className="builder__navbtn"
              onClick={() => {
                setStandingDown(true);
                setFailure(null);
              }}
            >
              Stand down…
            </button>
          </div>
        )
      ) : null}

      {/* One sentence, and nothing under it. A refused act never names why. */}
      {failure !== null ? (
        <p className="surface__state surface__state--error" role="alert">
          {failure}
        </p>
      ) : null}
    </article>
  );
}

function LeadBlock({
  signal,
  canWrite,
  busy,
  handingOff,
  reason,
  reasonCountId,
  reasonReady,
  reasonBlocker,
  onReason,
  onStartHandOff,
  onCancelHandOff,
  onTake,
  onHandOff,
  onRelease,
}: {
  signal: SosSignal;
  canWrite: boolean;
  busy: boolean;
  handingOff: boolean;
  reason: string;
  reasonCountId: string;
  reasonReady: boolean;
  reasonBlocker: string | null;
  onReason: (value: string) => void;
  onStartHandOff: () => void;
  onCancelHandOff: () => void;
  onTake: () => void;
  onHandOff: () => void;
  onRelease: () => void;
}) {
  const held = signal.lead !== null;
  const heldByCaller = signal.callerIsLead;

  return (
    <div className="sos__lead">
      <h3 className="sos__lead-title">Lead</h3>
      {held ? (
        <p className="sos__lead-who" role="status">
          Current lead: {signal.lead?.memberLabel ?? "a supervisor"}
        </p>
      ) : (
        <p className="sos__lead-who" role="status">
          No supervisor holds the lead.
        </p>
      )}

      {canWrite && !held ? (
        <button
          type="button"
          className="builder__navbtn"
          disabled={busy || !signal.callerAcknowledged}
          onClick={onTake}
        >
          Take the lead
        </button>
      ) : null}

      {canWrite && held && heldByCaller ? (
        <button type="button" className="builder__navbtn" disabled={busy} onClick={onRelease}>
          Release lead
        </button>
      ) : null}

      {/* Taking the lead from another supervisor is a hand-off: it needs a reason and it
          appends one fact naming both people. There is no approve or decline step, because
          life safety never waits on a second person's consent. */}
      {canWrite && held && !heldByCaller ? (
        handingOff ? (
          <div className="sos__handoff">
            <label className="sos__note">
              Reason · required, internal
              <textarea
                rows={2}
                value={reason}
                maxLength={STAND_DOWN_NOTE_MAX}
                aria-describedby={reasonCountId}
                onChange={(event) => onReason(event.target.value)}
              />
            </label>
            <p className="sos__counter" id={reasonCountId}>
              {reason.trim().length} / {STAND_DOWN_NOTE_MAX}
            </p>
            <div className="sos__actions">
              <button
                type="button"
                className="sos__handoff-go"
                disabled={busy || !reasonReady}
                onClick={onHandOff}
              >
                Hand off lead
              </button>
              <button
                type="button"
                className="builder__navbtn"
                disabled={busy}
                onClick={onCancelHandOff}
              >
                Keep lead
              </button>
            </div>
            {reasonBlocker !== null ? <p className="sos__blocker">{reasonBlocker}</p> : null}
          </div>
        ) : (
          <button
            type="button"
            className="builder__navbtn"
            disabled={busy || !signal.callerAcknowledged}
            onClick={onStartHandOff}
          >
            Take the lead
          </button>
        )
      ) : null}

      <p className="sos__lead-note">
        The lead is a label for your team, not a lock. Anyone with access can still acknowledge
        or stand down.
      </p>
    </div>
  );
}

function SosTimeline({
  entries,
  timeZone,
  logUnavailable,
}: {
  entries: readonly ReturnType<typeof buildSosTimeline>[number][];
  timeZone: string;
  logUnavailable: boolean;
}) {
  // P-26: a failed log read is a sentence and no rows. A one-line timeline of a signal
  // several people worked is a false record, not a partial one.
  if (logUnavailable) {
    return (
      <div className="sos__log-out">
        <p className="surface__state" role="status">
          {SOS_LOG_UNAVAILABLE}
        </p>
        <p className="sos__log-warn">{SOS_LOG_UNAVAILABLE_DETAIL}</p>
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <p className="surface__state" role="status">
        Nothing logged for this SOS yet.
      </p>
    );
  }

  return (
    <>
      <h3 className="sos__subtitle">Recorded so far</h3>
      <ol className="sos__timeline" aria-label="SOS log">
        {entries.map((entry) => (
          <li key={entry.key} className="sos__event">
            <span className="sos__event-time">
              {entry.at !== null ? formatDisplayTime(entry.at, timeZone) : "time not recorded"}
            </span>
            <span className="sos__event-text">{entry.text}</span>
            {entry.actor !== null ? <span className="sos__event-actor">{entry.actor}</span> : null}
            {entry.reason !== null ? (
              <span className="sos__event-reason">Reason: {entry.reason}</span>
            ) : null}
          </li>
        ))}
      </ol>
    </>
  );
}

function ClearedList({
  alerts,
  log,
  timeZone,
  nowMs,
}: {
  alerts: readonly SosSignal[];
  log: readonly SosLogEntry[] | null;
  timeZone: string;
  nowMs: number;
}) {
  return (
    <div className="sos__cleared">
      <h3 className="sos__subtitle">Recently cleared</h3>
      <ul className="sos__cleared-list">
        {alerts.map((signal) => {
          const age = formatAge(signal.acknowledgedAt, nowMs);
          return (
            <li key={signal.alertEventId} className="sos__cleared-row">
              <span className="sos__cleared-who">{signal.guardLabel ?? "Officer"}</span>
              <span className="sos__cleared-post">{postLabel(signal)}</span>
              {age !== null ? <span className="sos__age">cleared {age} ago</span> : null}
              {/* Amber for every outcome, including Officer safe, resolved. Proof green is
                  reserved for what a device or a person actually confirmed, and coral is a
                  failed act. A green False alarm would read as a good result rather than a
                  recorded one. */}
              {signal.standDownOutcome !== null ? (
                <span className="sos__outcome-chip">
                  {standDownOutcomeLabel(signal.standDownOutcome)}
                </span>
              ) : null}
              <SosTimeline
                entries={log === null ? [] : buildSosTimeline(signal, log)}
                timeZone={timeZone}
                logUnavailable={log === null}
              />
            </li>
          );
        })}
      </ul>
      <p className="sos__cleared-note">
        Three most recently cleared signals from the readable set.
      </p>
    </div>
  );
}

interface SosDeskLoad {
  /** Binds the payload to the account and the support context that produced it. */
  securityContextKey: string;
  queue: Awaited<ReturnType<typeof fetchSosSignalQueue>>;
  log: readonly SosLogEntry[] | null;
}

export function SosResponseDeskPanel(props: {
  client: SupabaseClient;
  accountId: string;
  timeZone: string;
  pollMs?: number;
  selectedAlertEventId?: string | null;
}) {
  const { session } = useSupportSession();
  const supportActive = isSupportSessionActive(session);
  const contextKey = props.accountId + ":" + (supportActive ? (session?.sessionId ?? "support") : "direct");
  const policy = useReadModel(async () => ({
    contextKey,
    enabled: await fetchSosCreationState(props.client, props.accountId),
  }), [props.client, contextKey], props.pollMs);
  const current = policy.data?.contextKey === contextKey ? policy.data : null;
  if (policy.status === "ready" && current?.enabled === true) {
    return <ActiveSosResponseDeskPanel {...props} />;
  }
  return <SosResponseOnlyPanel key={contextKey} client={props.client} accountId={props.accountId}
    contextKey={contextKey} supportActive={supportActive}
    policyState={policy.status === "ready" && current?.enabled === false ? "paused" : policy.status === "error" ? "unavailable" : "loading"}
    {...(props.pollMs !== undefined ? { pollMs: props.pollMs } : {})}
    selectedAlertEventId={props.selectedAlertEventId ?? null} />;
}

function ActiveSosResponseDeskPanel({
  client,
  accountId,
  timeZone,
  pollMs,
  selectedAlertEventId = null,
}: {
  client: SupabaseClient;
  accountId: string;
  timeZone: string;
  pollMs?: number;
  selectedAlertEventId?: string | null;
}) {
  const { session } = useSupportSession();
  const supportActive = isSupportSessionActive(session);
  // Account, membership and support changes all change this key, which re-runs the read and
  // makes every response from the previous context stale by construction.
  const securityContextKey = `${accountId}:${supportActive ? (session?.sessionId ?? "support") : "direct"}`;

  const { status, data, reload } = useReadModel<SosDeskLoad>(
    async () => {
      const queue = await fetchSosSignalQueue(client, accountId);
      const ids = queue.status === "ok" ? queue.rows.map((row) => row.alertEventId) : [];
      const log = await fetchSosResponseLog(client, accountId, ids);
      return {
        securityContextKey,
        queue,
        log: log.status === "ok" ? log.rows : null,
      };
    },
    [client, securityContextKey],
    pollMs,
  );

  const current = data !== null && data.securityContextKey === securityContextKey ? data : null;

  if (current === null) {
    if (status === "error") {
      return (
        <UnavailableNotice detail="Treat SOS status as unknown rather than clear." />
      );
    }
    return (
      <p className="surface__state" role="status">
        Loading SOS status...
      </p>
    );
  }

  // Denied, absent, malformed and over-cap reads are all unavailable, and none of them is
  // ever dressed as an empty band or a shortened list. Only the second sentence differs.
  if (current.queue.status === "unavailable") {
    return (
      <UnavailableNotice
        detail={
          current.queue.reason === "capped"
            ? SOS_QUEUE_CAPPED_DETAIL
            : SOS_QUEUE_UNAVAILABLE_DETAIL
        }
      />
    );
  }

  const signals = current.queue.status === "ok" ? current.queue.rows : [];

  return (
    <>
      {status === "error" ? (
        <p className="surface__state surface__state--error" role="alert">
          Could not refresh SOS status. This is the last reading, so it may be out of date.
        </p>
      ) : null}
      {/* Keyed on the security context: an account or support switch discards every draft
          outcome, note and hand-off reason instead of carrying it into the new context. */}
      <SosResponseDesk
        key={securityContextKey}
        client={client}
        accountId={accountId}
        signals={signals}
        log={current.log}
        timeZone={timeZone}
        onReviewed={reload}
        canWrite={!supportActive}
        selectedAlertEventId={selectedAlertEventId}
      />
    </>
  );
}

function UnavailableNotice({ detail }: { detail: string }) {
  return (
    <div className="sos__unavailable">
      <p className="surface__state surface__state--error" role="alert">
        {SOS_QUEUE_UNAVAILABLE}
      </p>
      <p className="sos__log-warn">{detail}</p>
    </div>
  );
}
